import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { emailService } from '../../../services/emailService';
import { sendAdminTransactionNotification } from '../../../services/emailService';

const StripePaymentForm = ({ onSuccess, amount, termsAccepted, bookingInfo, trailerData, legalAcknowledgments }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const saveBookingToDatabase = async (paymentIntent) => {
    const { data: { user } } = await supabase?.auth?.getUser();

    if (!user) throw new Error('User not authenticated. Please log in and try again.');

    const start = new Date(bookingInfo?.startDate);
    const end = new Date(bookingInfo?.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    let subtotal = days * (trailerData?.pricing?.dailyRate || 0);
    let discount = 0;

    if (days >= 7 && trailerData?.pricing?.weeklyDiscount) {
      discount = subtotal * (trailerData?.pricing?.weeklyDiscount / 100);
    }

    const cleaningFee = trailerData?.pricing?.cleaningFee || 0;
    const securityDeposit = trailerData?.pricing?.securityDeposit || 0;
    const taxAmount = (subtotal - discount + cleaningFee) * 0.0825;
    const total = subtotal - discount + securityDeposit + cleaningFee + taxAmount;

    const bookingReference = `BK-${Date.now()}`;

    const { data: booking, error: bookingError } = await supabase
      ?.from('bookings')
      ?.insert({
        booking_reference: bookingReference,
        renter_id: user?.id,
        owner_id: trailerData?.ownerId,
        trailer_id: trailerData?.id,
        trailer_listing_id: trailerData?.id,
        start_date: bookingInfo?.startDate,
        end_date: bookingInfo?.endDate,
        status: 'confirmed',
        daily_rate: trailerData?.pricing?.dailyRate,
        total_days: days,
        subtotal: subtotal,
        discount: discount,
        tax: taxAmount,
        security_deposit: securityDeposit,
        cleaning_fee: cleaningFee,
        total_amount: total,
        payment_intent_id: paymentIntent?.id,
        payment_status: 'succeeded',
        stripe_customer_id: paymentIntent?.customer,
        tax_amount: taxAmount,
        acknowledge_marketplace_only: legalAcknowledgments?.acknowledgeMarketplaceOnly || false,
        acknowledge_no_insurance: legalAcknowledgments?.acknowledgeNoInsurance || false,
        agree_rental_agreement: legalAcknowledgments?.agreeRentalAgreement || false,
        agree_arbitration_waive_class_action: legalAcknowledgments?.agreeArbitrationWaiveClassAction || false
      })
      ?.select()
      ?.single();

    if (bookingError) throw bookingError;

    // Fetch profiles for email notifications
    const [ownerProfileResult, renterProfileResult] = await Promise.all([
      supabase?.from('user_profiles')?.select('full_name, email, phone, profile_photo_url, verified_status, city, state')?.eq('id', trailerData?.ownerId)?.single(),
      supabase?.from('user_profiles')?.select('full_name, email')?.eq('id', user?.id)?.single()
    ]);

    const ownerProfile = ownerProfileResult?.data;
    const renterProfile = renterProfileResult?.data;

    // Send booking confirmation email — wrapped so email failure doesn't break booking
    try {
      if (renterProfile?.email) {
        await emailService?.sendBookingConfirmation({
          recipientEmail: renterProfile?.email,
          recipientName: renterProfile?.full_name,
          bookingData: {
            bookingReference: booking?.booking_reference,
            startDate: bookingInfo?.startDate,
            endDate: bookingInfo?.endDate,
            totalAmount: total?.toFixed(2)
          },
          trailerData: {
            title: trailerData?.title,
            type: trailerData?.type,
            location: trailerData?.location
          },
          ownerData: {
            name: ownerProfile?.full_name || 'Owner',
            email: ownerProfile?.email || '',
            phone: ownerProfile?.phone || ''
          }
        });
      }
    } catch (emailErr) {
      console.warn('Booking confirmation email failed (non-critical):', emailErr);
    }

    // Notify admin of new transaction (non-blocking)
    try {
      await sendAdminTransactionNotification({
        bookingReference: booking?.booking_reference,
        totalAmount: total?.toFixed(2),
        paymentIntentId: paymentIntent?.id,
        renterName: renterProfile?.full_name || 'Unknown',
        renterEmail: renterProfile?.email || '',
        trailerTitle: trailerData?.title,
        trailerType: trailerData?.type,
        trailerLocation: trailerData?.location,
        startDate: bookingInfo?.startDate,
        endDate: bookingInfo?.endDate
      });
    } catch (notifyErr) {
      console.warn('Admin transaction notification failed (non-critical):', notifyErr);
    }

    return { booking, ownerProfile, renterProfile, total, days, subtotal, discount, taxAmount, securityDeposit, cleaningFee, bookingReference };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) return;

    if (!termsAccepted) {
      setError('Please accept all required acknowledgments to proceed');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/booking-confirmation`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError?.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        const bookingResult = await saveBookingToDatabase(paymentIntent);
        onSuccess(paymentIntent, bookingResult);
      } else {
        setError('Payment was not completed. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err?.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center space-x-2">
        <Icon name="CreditCard" size={24} strokeWidth={2} className="text-primary" />
        <span>Payment Details</span>
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <PaymentElement />

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} strokeWidth={2} color="var(--color-destructive)" />
            <p className="font-body text-sm text-destructive">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          disabled={!stripe || !elements || isProcessing || !termsAccepted}
          iconName={isProcessing ? 'Loader' : 'Lock'}
          iconPosition="left"
        >
          {isProcessing ? 'Processing Payment...' : `Pay $${amount?.toFixed(2)}`}
        </Button>

        <div className="flex items-center justify-center space-x-2 text-muted-foreground">
          <Icon name="Shield" size={16} strokeWidth={2} />
          <p className="font-caption text-xs">Secured by Stripe — 256-bit SSL encryption</p>
        </div>
      </form>
    </div>
  );
};

export default StripePaymentForm;
